import { AsyncPipe, NgClass, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import { Dossier, Fitter } from '@sc/data-access-models';
import {
  EnumJsonObjectKey,
  JsonObjectKey,
  StorageService,
} from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import {
  CalendarHiddenDays,
  CalendarMode,
  FittersManagerService,
  defaultHiddenDays,
} from '@sc/feature-fitter-shared-libs';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import {
  ButtonComponent,
  IconComponent,
  SplitButton,
  SplitButtonComponent,
} from '@sc/ui-components';
import { dayjs, isToday } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { BaseAbstractDirective } from '../../../base-abstract';
import { CalendarAction } from '../../../models';
import { PlanningSandboxService } from '../../../services';
interface DossiersSeen {
  previousDossiers: string[];
  seen: boolean;
}

@Component({
  selector: 'sc-planning-calendar-header',
  styleUrls: ['./planning-calendar-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    IconComponent,
    SplitButtonComponent,
    MatMenuModule,
    MatDatepickerModule,
    NgClass,
    ButtonComponent,
    AsyncPipe,
    TitleCasePipe,
    TranslateModule,
  ],
  template: ` <div class="container">
      <div
        *ngIf="!isLandscapeView"
        class="slider filter"
        (click)="event(CalendarAction.TOGGLE_FILTER)"
      >
        <sc-icon name="big_arrow_left" [class.closed]="!filterOpen"></sc-icon>
      </div>
      <div class="controls">
        <div class="navigation" *ngIf="!isLandscapeView">
          <sc-split-button [splitButtons]="arrowSplitButtons"></sc-split-button>
          <div
            class="header-date"
            mat-button
            [matMenuTriggerFor]="calendarWidget"
            #calenderTrigger="matMenuTrigger"
          >
            <div class="day">{{ day }}</div>
            <div class="week">
              {{ week }}
            </div>
          </div>
          <mat-menu class="widget-calendar" #calendarWidget="matMenu">
            <mat-calendar
              #calendarDatePicker
              [(selected)]="date"
              (selectedChange)="dateChanged($event)"
              (click)="stopPropagation($event)"
            ></mat-calendar>
          </mat-menu>
        </div>
        <div class="mobile-nav-bar" *ngIf="isLandscapeView">
          <div class="menu-burger" (click)="toggleMobileNavBar(true)">
            <sc-icon class="mobile-menu-toggle" name="menu"></sc-icon>
          </div>
          <span class="brand-span">
            <sc-icon name="arrow_left" (click)="onGoBack()"></sc-icon>
            <span class="header-fitter">{{ fitters[0]?.brand }}</span>
          </span>

          <sc-icon
            class="arrow left"
            name="arrow_left"
            tabindex="0"
            (click)="event(CalendarAction.PREV)"
          ></sc-icon>
          <div
            class="landscape-day"
            [matMenuTriggerFor]="calendarWidget"
            #calenderTrigger="matMenuTrigger"
          >
            <span>{{ weekDay | titlecase }}</span>
            <span
              [ngClass]="{
                today: isToday()
              }"
              >{{ monthDay }}</span
            >
            <span>{{ montYear | titlecase }}</span>
          </div>

          <sc-icon
            class="arrow right"
            name="arrow_right"
            tabindex="0"
            (click)="event(CalendarAction.NEXT)"
          ></sc-icon>
          <sc-icon
            class="user-icon"
            name="user"
            (click)="toggleMobileNavBar(false)"
          ></sc-icon>
          <mat-menu class="widget-calendar" #calendarWidget="matMenu">
            <mat-calendar
              #calendarDatePicker
              [(selected)]="date"
              (selectedChange)="dateChanged($event)"
              (click)="stopPropagation($event)"
            ></mat-calendar>
          </mat-menu>
        </div>

        <div class="actions" *ngIf="!isLandscapeView">
          <sc-button
            *ngIf="!formOpen"
            type="button"
            buttonType="mainWhite"
            leftIcon="plus"
            class="plus"
            [disabled]="!(fittersManagerService.fitters$ | async)?.length"
            [iconOnly]="true"
            [matMenuTriggerFor]="menu"
          >
          </sc-button>
          <mat-menu class="new-intervention" #menu="matMenu" xPosition="before">
            <button mat-menu-item (click)="event(CalendarAction.INTERVENTION)">
              <sc-icon name="wrench"></sc-icon>
              <span>
                {{ 'PlanningHeader.newInterventionBtn' | translate }}</span
              >
            </button>
            <button mat-menu-item (click)="event(CalendarAction.EVENT)">
              <sc-icon name="book_appointment"></sc-icon>
              <span> {{ 'PlanningHeader.newEventBtn' | translate }}</span>
            </button>
            <button
              *ngIf="hasSettingsAccess()"
              mat-menu-item
              (click)="event(CalendarAction.EXCEPTIONAL_CLOSING)"
            >
              <sc-icon name="XCircle"></sc-icon>
              <span> {{ 'PlanningHeader.closing' | translate }}</span>
            </button>
            <button
              *ngIf="hasSettingsAccess()"
              mat-menu-item
              (click)="event(CalendarAction.UNAVAILABILITY)"
            >
              <sc-icon name="planning"></sc-icon>
              <span> {{ 'PlanningHeader.unavailabilityBtn' | translate }}</span>
            </button>
          </mat-menu>
          <sc-button
            type="button"
            buttonType="mainWhite"
            class="today"
            (click)="event(CalendarAction.TODAY)"
          >
            <div class="button-label">
              {{ 'PlanningHeader.today' | translate }}
            </div>
          </sc-button>

          <sc-split-button
            *ngIf="displaySplitButtons"
            [splitButtons]="splitButtons"
            [activeButtonIndex]="getCalendarModeBtnIndex()"
          ></sc-split-button>

          <div class="zoom-btn-container">
            <div class="zoom-btn-row zoom-btn-row-vertical">
              <span class="zoom-direction-indicator">
                <sc-icon name="lines_horizontal"></sc-icon>
              </span>
              <button
                class="btn-zoom-out"
                type="button"
                (click)="event(CalendarAction.ZOOM_OUT_H)"
              >
                <sc-icon name="zoom_out"></sc-icon>
              </button>
              <button
                class="btn-zoom-in"
                type="button"
                (click)="event(CalendarAction.ZOOM_IN_H)"
              >
                <sc-icon name="zoom_in"></sc-icon>
              </button>
            </div>
            <div class="zoom-btn-row zoom-btn-row-horizontal">
              <span class="zoom-direction-indicator">
                <sc-icon name="lines_horizontal"></sc-icon>
              </span>
              <button
                class="btn-zoom-out"
                type="button"
                (click)="event(CalendarAction.ZOOM_OUT_W)"
              >
                <sc-icon name="zoom_out"></sc-icon>
              </button>
              <button
                class="btn-zoom-in"
                type="button"
                (click)="event(CalendarAction.ZOOM_IN_W)"
              >
                <sc-icon name="zoom_in"></sc-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        *ngIf="!formOpen && !isLandscapeView"
        class="slider basket"
        (click)="event(CalendarAction.TOGGLE_BASKET)"
      >
        <div
          *ngIf="displayApmtRequestIndicator()"
          class="basket-item-indicator apmt-request-indicator"
          [class.highlight]="highlightAppointmentRequestIndicator()"
        >
          <sc-icon name="folder"></sc-icon>
          <div class="item-number-circle">
            {{ nbApmtRequests }}
          </div>
        </div>
        <div
          class="basket-item-indicator external-dossiers-indicator"
          [class.highlight]="highlightExternalDossierIndicator()"
        >
          <sc-icon name="inbox"></sc-icon>
          <div class="item-number-circle">
            {{ nbExternalDossiers }}
          </div>
        </div>
      </div>
    </div>
    <div class="fitter-navbar-container" [class.active]="isMobileNavActive">
      <div class="leftSection">
        <div class="menu" *ngIf="isMobileNavType">
          <sc-button *ngIf="hasPlanningAccess()" (click)="goToPlanning()">{{
            'FeatureFitterNavBar.NavBar.planning' | translate
          }}</sc-button>
          <sc-button *ngIf="hasSettingsAccess()" (click)="goToSettings()">{{
            'FeatureFitterNavBar.NavBar.settings' | translate
          }}</sc-button>
          <sc-button *ngIf="hasSearchAccess()" (click)="goToSearchDossiers()">{{
            'FeatureFitterNavBar.NavBar.searchPage' | translate
          }}</sc-button>
          <sc-button *ngIf="hasDashboardAccess()" (click)="goToDashboard()">{{
            'FeatureFitterNavBar.NavBar.dashboard' | translate
          }}</sc-button>
        </div>
        <div
          class="mobile-logout"
          *ngIf="isMobileNavType === false"
          (click)="logoutUser()"
        >
          <sc-icon name="logout" id="user-logout-icon"></sc-icon>
          {{ 'FeatureFitterNavBar.NavBar.logout' | translate }}
        </div>
      </div>
    </div>`,
})
export class PlanningCalendarHeaderComponent
  extends BaseAbstractDirective
  implements OnChanges, OnInit
{
  static readonly defaultDossiersSeen: DossiersSeen = {
    previousDossiers: [],
    seen: true,
  };

  @Output() calendarEvent = new EventEmitter<CalendarAction>();
  @Output() datePickerEvent = new EventEmitter<Date>();
  @Input() date: Date | string;
  @Input() basketOpen: boolean;
  @Input() formOpen: boolean;

  @Input() isLandscapeView = true;
  @Input() filterOpen: boolean;
  @Input() mode: CalendarMode;
  @ViewChild('calenderTrigger') calendarMenu: MatMenuTrigger;
  @ViewChild('calendarDatePicker') calendarDatePicker: MatCalendar<Dayjs>;

  nbExternalDossiers = 0;
  nbApmtRequests = 0;

  weekDay: string;
  montYear: string;
  monthDay: string;

  currentExternalDossiersSeen: DossiersSeen =
    PlanningCalendarHeaderComponent.defaultDossiersSeen;
  currentApmtRequestSeen: DossiersSeen =
    PlanningCalendarHeaderComponent.defaultDossiersSeen;

  displaySplitButtons: boolean;
  splitButtons: SplitButton[] = [
    {
      label: this.translator.getInstantTranslation('PlanningHeader.day'),
      action: () => this.event(CalendarAction.DAYS),
    },
    {
      label: this.translator.getInstantTranslation('PlanningHeader.workWeek'),
      action: () => this.event(CalendarAction.WORK_WEEKS),
    },
  ];
  arrowSplitButtons: SplitButton[] = [
    {
      icon: 'arrow_left',
      action: () => this.event(CalendarAction.PREV),
    },
    {
      icon: 'arrow_right',
      action: () => this.event(CalendarAction.NEXT),
    },
  ];

  week: string;
  day: string;

  fitters: Fitter[] = [];
  isMobileNavActive: boolean;
  isMobileNavType: boolean;

  constructor(
    injector: Injector,
    private readonly translator: TranslatorService,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly configService: ConfigurationService,
    private readonly featureFlipService: FeatureFlippingManagerService,
    private readonly storage: StorageService,
    private readonly authService: AuthService,
    public fittersManagerService: FittersManagerService,
    private readonly routerService: FitterRouterService,
    private readonly route: ActivatedRoute,
    private readonly timezoneService: TimezoneService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.currentExternalDossiersSeen = this.storage.getItem<
      JsonObjectKey,
      DossiersSeen
    >(
      'seen-external-dossiers-basket',
      localStorage,
      PlanningCalendarHeaderComponent.defaultDossiersSeen
    );
    this.currentApmtRequestSeen = this.storage.getItem<
      JsonObjectKey,
      DossiersSeen
    >(
      'seen-apmt-requests-basket',
      localStorage,
      PlanningCalendarHeaderComponent.defaultDossiersSeen
    );

    const daysToHide = this.storage.getItem<JsonObjectKey, CalendarHiddenDays>(
      'calendar-hidden-days',
      localStorage,
      { ...defaultHiddenDays }
    );
    if (Object.values(daysToHide).filter((hidden) => !hidden).length > 1) {
      //There are more than one day not hidden
      this.displaySplitButtons = true;
    }

    this.subs.externalDossiersToValidate =
      this.planningSandbox.shownExternalDossiersToValidate$.subscribe(
        (dossiers) => {
          this.computeExternalDossiersSeen(dossiers);

          this.nbExternalDossiers = dossiers?.length ?? 0;
          this.changeDetectorRef.markForCheck();
        }
      );

    this.subs.externalAppointmentRequests =
      this.planningSandbox.shownExternalAppointmentRequests$.subscribe(
        (dossiers) => {
          this.computeApmtRequestsSeen(dossiers);

          this.nbApmtRequests = dossiers?.length ?? 0;
          this.changeDetectorRef.markForCheck();
        }
      );

    this.fittersManagerService
      .getOrFetchCurrentFitters()
      .subscribe((fitters) => {
        if (this.isLandscapeView) {
          this.route.params.subscribe((params) => {
            if (params?.fitterId) {
              this.fitters = fitters.filter(
                (fitter) => fitter.id === params.fitterId
              );
            }
          });
        } else {
          this.fitters = fitters;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.date || changes.mode) {
      this.computeDateValues(changes.date?.currentValue || this.date);
    }

    // Set all dossier to seen if basket is being openned
    if (!!changes.basketOpen && !!changes.basketOpen.currentValue) {
      this.triggerSeeAllDossiers();
    }
  }

  triggerSeeAllDossiers() {
    this.currentExternalDossiersSeen.seen = true;
    this.storage.setItem(
      'seen-external-dossiers-basket',
      this.currentExternalDossiersSeen,
      localStorage,
      PlanningCalendarHeaderComponent.defaultDossiersSeen
    );

    this.currentApmtRequestSeen.seen = true;
    this.storage.setItem(
      'seen-apmt-requests-basket',
      this.currentApmtRequestSeen,
      localStorage,
      PlanningCalendarHeaderComponent.defaultDossiersSeen
    );
  }

  computeExternalDossiersSeen(dossiers: Dossier[]) {
    this.computeDossierListSeen(
      dossiers,
      this.currentExternalDossiersSeen,
      EnumJsonObjectKey.SEEN_EXTERNAL_DOSSIERS_BASKET
    );
  }

  computeApmtRequestsSeen(dossiers: Dossier[]) {
    this.computeDossierListSeen(
      dossiers,
      this.currentApmtRequestSeen,
      EnumJsonObjectKey.SEEN_APMT_REQUESTS_BASKET
    );
  }

  computeDossierListSeen(
    arrivingDossiers: Dossier[],
    dossiersSeen: DossiersSeen,
    storageKey: EnumJsonObjectKey
  ) {
    const newDossiers = arrivingDossiers
      .filter((d) => !dossiersSeen.previousDossiers.includes(d.id))
      .map((d) => d.id);

    if (newDossiers.length > 0 && !this.basketOpen) {
      dossiersSeen.seen = false;
    }

    if (arrivingDossiers.length > 0) {
      dossiersSeen.previousDossiers = arrivingDossiers.map((d) => d.id);
    }

    this.storage.setItem(
      storageKey,
      dossiersSeen,
      localStorage,
      PlanningCalendarHeaderComponent.defaultDossiersSeen
    );
  }

  computeDateValues = (date: Date) => {
    const timezone = this.timezoneService.getFitterListTimezone(
      ...this.fitters
    );

    const usedDate = dayjs(date).tz(timezone).utc();

    if (this.mode === 'resourceTimeGridDay') {
      if (this.isLandscapeView) {
        this.weekDay = usedDate.format('dddd');
        this.montYear = usedDate.format('MMMM YYYY');
        this.monthDay = usedDate.format('DD');
      } else {
        this.day = usedDate.format('dddd LL');
        this.week = `
      ${this.translator.getInstantTranslation('PlanningHeader.week')}
      ${usedDate.format('w')}
        `;
      }
    } else if (this.mode === 'resourceTimeGridWeek') {
      const weekDate = usedDate.startOf('isoWeek');

      this.day = `
        ${this.translator.getInstantTranslation('PlanningHeader.week')}
        ${weekDate.format('w')}
      `;
      this.week = `
      ${this.translator.getInstantTranslation('PlanningAgenda.from')}
      ${weekDate.format('LL')}
        ${this.translator.getInstantTranslation('PlanningAgenda.to')}
        ${weekDate.add(6, 'd').format('LL')}
        `;
    }
    if (this.calendarDatePicker != null) {
      this.calendarDatePicker.activeDate = usedDate.clone();
      this.calendarDatePicker.updateTodaysDate();
    }
    this.changeDetectorRef.markForCheck();
  };

  event = (action: CalendarAction) => {
    this.calendarEvent.emit(action);
  };

  get CalendarAction() {
    return CalendarAction;
  }

  highlightExternalDossierIndicator() {
    return !this.basketOpen && !this.currentExternalDossiersSeen.seen;
  }

  highlightAppointmentRequestIndicator() {
    return !this.basketOpen && !this.currentApmtRequestSeen.seen;
  }

  getCalendarModeBtnIndex() {
    if (this.mode === 'resourceTimeGridDay') {
      return 0;
    }
    return 1;
  }

  hasSettingsAccess() {
    return this.authService.userHasFitterSettingsAccess$(this.fitters);
  }

  displayApmtRequestIndicator() {
    return (
      this.featureFlipService.isBrandFeatureActivated(
        this.configService.getConfig().id,
        'b2b-allow-appointment-request'
      ) ||
      this.featureFlipService.isBrandFeatureActivated(
        this.configService.getConfig().id,
        'b2c-allow-appointment-request'
      )
    );
  }

  dateChanged(newDate: Dayjs) {
    const adjustedDate = newDate.add(newDate?.utcOffset() / 60, 'hours');
    this.datePickerEvent.emit(adjustedDate.toDate());
    this.calendarMenu.closeMenu();
  }

  stopPropagation($event) {
    $event.stopPropagation();
  }

  onGoBack() {
    this.routerService.goToPlanning();
  }

  toggleMobileNavBar(isMobileNavType) {
    this.isMobileNavActive = !this.isMobileNavActive;
    this.isMobileNavType = isMobileNavType;
  }

  goToPlanning() {
    this.routerService.goToPlanning();
  }

  goToSettings() {
    this.routerService.goToSettings();
  }

  goToSearchDossiers() {
    this.routerService.goToSearchDossiers();
  }

  goToDashboard() {
    this.routerService.goToDashboard();
  }

  hasPlanningAccess() {
    return true;
  }

  hasDashboardAccess() {
    return true;
  }
  hasSearchAccess() {
    return true;
  }
  async logoutUser() {
    await this.authService.logout();
    location.reload();
  }

  isToday() {
    const date = this.date ?? new Date();
    return isToday(date);
  }
}
